import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Zap, Truck, Cog, Wrench, Bike, Box, Settings, Sticker } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import SearchBar from '../components/SearchBar';
import ProductCarousel from '../components/ProductCarousel';
import SEO from '../components/SEO';
import PromoVideo from '../components/PromoVideo';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Custom SVG Icons - All in KTM Orange (#FF6600)
const BrakeIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    <circle cx="16" cy="16" r="12" />
    <circle cx="16" cy="16" r="7" />
    <path d="M 8 10 L 4 6 M 24 10 L 28 6" strokeLinecap="round"/>
    <rect x="3" y="14" width="6" height="4" rx="1" fill="#FF6600"/>
    <rect x="23" y="14" width="6" height="4" rx="1" fill="#FF6600"/>
  </svg>
);

const SuspensionIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    {/* Forcella telescopica moto */}
    <line x1="12" y1="6" x2="12" y2="26" strokeWidth="3"/>
    <line x1="20" y1="6" x2="20" y2="26" strokeWidth="3"/>
    <rect x="8" y="4" width="16" height="6" rx="1" fill="#FF6600" opacity="0.3"/>
    <circle cx="12" cy="18" r="2" fill="#FF6600"/>
    <circle cx="20" cy="18" r="2" fill="#FF6600"/>
    <path d="M 8 12 L 8 14 M 24 12 L 24 14" strokeWidth="2" strokeLinecap="round"/>
    <path d="M 8 20 L 8 22 M 24 20 L 24 22" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const PlasticFenderIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    {/* Parafango/Carenatura */}
    <path d="M 6 20 Q 6 12 16 8 Q 26 12 26 20" fill="#FF6600" opacity="0.2"/>
    <path d="M 6 20 Q 6 12 16 8 Q 26 12 26 20" strokeLinecap="round"/>
    <path d="M 16 8 L 16 26" stroke="#FF6600" strokeWidth="1.5" strokeDasharray="2,2"/>
    <ellipse cx="16" cy="20" rx="10" ry="3" fill="#FF6600" opacity="0.3"/>
    <path d="M 10 14 Q 10 12 16 10 Q 22 12 22 14" strokeWidth="1.5"/>
  </svg>
);

const ExhaustIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    <rect x="4" y="12" width="20" height="8" rx="2"/>
    <path d="M 24 14 L 28 14 L 28 18 L 24 18" fill="#FF6600"/>
    <line x1="8" y1="16" x2="20" y2="16" strokeWidth="1"/>
    <circle cx="6" cy="16" r="1" fill="#FF6600"/>
    <circle cx="10" cy="16" r="1" fill="#FF6600"/>
    <circle cx="14" cy="16" r="1" fill="#FF6600"/>
  </svg>
);

const RiderGearIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    <circle cx="16" cy="8" r="3" fill="#FF6600"/>
    <path d="M 16 11 L 16 20 M 10 14 L 16 16 L 22 14" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M 16 20 L 12 28 M 16 20 L 20 28" strokeLinecap="round"/>
    <rect x="14" y="16" width="4" height="6" rx="1" fill="#FF6600" opacity="0.3"/>
  </svg>
);

const HelmetCrossIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    {/* Casco cross con visiera e mentoniera */}
    <path d="M 8 18 Q 8 8 16 4 Q 24 8 24 18 L 24 20 L 8 20 Z" fill="#FF6600" opacity="0.3"/>
    <path d="M 8 18 Q 8 8 16 4 Q 24 8 24 18" strokeLinecap="round"/>
    <line x1="8" y1="20" x2="24" y2="20" strokeWidth="3"/>
    {/* Visiera larga cross */}
    <path d="M 10 12 L 16 10 L 22 12 L 22 15 L 10 15 Z" fill="none" stroke="#FF6600" strokeWidth="1.5"/>
    {/* Mentoniera */}
    <path d="M 10 20 L 12 24 L 20 24 L 22 20" strokeWidth="2" strokeLinejoin="round"/>
  </svg>
);

const OilCanIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    {/* Tanica/Lattina olio stile motocross */}
    <rect x="8" y="6" width="16" height="4" rx="1" fill="#FF6600"/>
    <path d="M 10 10 L 10 24 Q 10 26 12 26 L 20 26 Q 22 26 22 24 L 22 10" fill="#FF6600" opacity="0.3"/>
    <path d="M 10 10 L 10 24 Q 10 26 12 26 L 20 26 Q 22 26 22 24 L 22 10"/>
    <rect x="14" y="14" width="4" height="8" rx="1" fill="#FF6600" opacity="0.5"/>
    <line x1="12" y1="18" x2="20" y2="18" strokeWidth="1.5"/>
    {/* Maniglia */}
    <path d="M 20 8 L 24 8 Q 26 8 26 10 L 26 12" strokeLinecap="round"/>
  </svg>
);

const TireIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    {/* Copertone MX tassellato */}
    <circle cx="16" cy="16" r="13" />
    <circle cx="16" cy="16" r="6" fill="#FF6600" opacity="0.25"/>
    <circle cx="16" cy="16" r="6" />
    <path d="M 16 3 L 16 7 M 16 25 L 16 29 M 3 16 L 7 16 M 25 16 L 29 16" strokeLinecap="round" strokeWidth="2.2"/>
    <path d="M 6.8 6.8 L 9.6 9.6 M 22.4 22.4 L 25.2 25.2 M 6.8 25.2 L 9.6 22.4 M 22.4 9.6 L 25.2 6.8" strokeLinecap="round" strokeWidth="1.5"/>
  </svg>
);

const LeverIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    {/* Leva freno/frizione con perno */}
    <path d="M 4 22 Q 4 20 6 20 L 24 8 Q 27 6 28 9 L 27 11 Q 26 12 24 12 L 8 24 Q 5 25 4 22 Z" fill="#FF6600" opacity="0.25"/>
    <path d="M 4 22 Q 4 20 6 20 L 24 8 Q 27 6 28 9 L 27 11 Q 26 12 24 12 L 8 24 Q 5 25 4 22 Z" strokeLinejoin="round"/>
    <circle cx="6" cy="22" r="2" fill="#FF6600"/>
    <path d="M 22 10 L 26 12" strokeWidth="1.2"/>
  </svg>
);

const AirFilterIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#FF6600" strokeWidth="2">
    {/* Filtro aria a pieghe */}
    <rect x="6" y="8" width="20" height="16" rx="2" fill="#FF6600" opacity="0.2"/>
    <rect x="6" y="8" width="20" height="16" rx="2"/>
    <path d="M 6 12 L 26 12 M 6 16 L 26 16 M 6 20 L 26 20" strokeWidth="1.2"/>
    <path d="M 9 8 L 9 24 M 13 8 L 13 24 M 17 8 L 17 24 M 21 8 L 21 24 M 25 8 L 25 24" strokeWidth="0.8" opacity="0.6"/>
    <circle cx="16" cy="16" r="2.5" fill="#FF6600"/>
  </svg>
);

const Home = () => {
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  
  // Comprehensive category list with CUSTOM ICONS
  // Categorie ordinate alfabeticamente (stesso ordine di Products.jsx)
  const allCategories = [
    { name: 'Abbigliamento', name_en: 'Apparel', name_es: 'Ropa', icon: RiderGearIcon },
    { name: 'Adesivi', name_en: 'Stickers', name_es: 'Adhesivos', icon: Sticker },
    { name: 'Filtri aria', name_en: 'Air Filters', name_es: 'Filtros de aire', icon: AirFilterIcon },
    { name: 'Freni', name_en: 'Brakes', name_es: 'Frenos', icon: BrakeIcon },
    { name: 'Leve', name_en: 'Levers', name_es: 'Palancas', icon: LeverIcon },
    { name: 'Lubrificanti', name_en: 'Lubricants', name_es: 'Lubricantes', icon: OilCanIcon },
    { name: 'Parti elettriche', name_en: 'Electronics', name_es: 'Eléctrica', icon: Zap },
    { name: 'Parti motore', name_en: 'Engine Parts', name_es: 'Motor', icon: Settings },
    { name: 'Plastiche', name_en: 'Plastics', name_es: 'Plásticos', icon: PlasticFenderIcon },
    { name: 'Pneumatici', name_en: 'Tires', name_es: 'Neumáticos', icon: TireIcon },
    { name: 'Protezioni moto', name_en: 'Protection', name_es: 'Protecciones', icon: Shield },
    { name: 'Ruote', name_en: 'Wheels', name_es: 'Ruedas', icon: Bike },
    { name: 'Sospensioni', name_en: 'Suspensions', name_es: 'Suspensiones', icon: SuspensionIcon },
    { name: 'Trasmissione', name_en: 'Transmission', name_es: 'Transmisión', icon: Cog },
  ];

  const [productCounts, setProductCounts] = React.useState({});
  const [offertaProducts, setOffertaProducts] = React.useState([]);
  const [novitaProducts, setNovitaProducts] = React.useState([]);
  const [outletProducts, setOutletProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const categoryScrollRef = React.useRef(null);

  React.useEffect(() => {
    // Smart scroll management: allow horizontal touch scroll, prevent vertical overflow
    const categoryContainer = categoryScrollRef.current;
    if (!categoryContainer) return;

    let touchStartX = 0;
    let touchStartY = 0;
    let isHorizontalScroll = false;

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isHorizontalScroll = false;
    };

    const handleTouchMove = (e) => {
      if (!touchStartX || !touchStartY) return;

      const touchMoveX = e.touches[0].clientX;
      const touchMoveY = e.touches[0].clientY;
      
      const deltaX = Math.abs(touchMoveX - touchStartX);
      const deltaY = Math.abs(touchMoveY - touchStartY);

      // Determine if user is scrolling horizontally
      if (!isHorizontalScroll && (deltaX > 5 || deltaY > 5)) {
        isHorizontalScroll = deltaX > deltaY;
      }

      // If scrolling horizontally on categories, prevent page scroll
      if (isHorizontalScroll) {
        e.stopPropagation();
      }
    };

    const handleTouchEnd = () => {
      touchStartX = 0;
      touchStartY = 0;
      isHorizontalScroll = false;
    };

    // Only prevent page scroll on wheel events (mouse/trackpad)
    const preventVerticalWheelScroll = (e) => {
      const isScrollingHorizontally = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (isScrollingHorizontally) {
        e.preventDefault();
        categoryContainer.scrollLeft += e.deltaX;
      }
    };

    categoryContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    categoryContainer.addEventListener('touchmove', handleTouchMove, { passive: false });
    categoryContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
    categoryContainer.addEventListener('wheel', preventVerticalWheelScroll, { passive: false });

    return () => {
      categoryContainer.removeEventListener('touchstart', handleTouchStart);
      categoryContainer.removeEventListener('touchmove', handleTouchMove);
      categoryContainer.removeEventListener('touchend', handleTouchEnd);
      categoryContainer.removeEventListener('wheel', preventVerticalWheelScroll);
    };
  }, []);

  React.useEffect(() => {
    // Fetch category counts
    fetch(`${BACKEND_URL}/api/categories`)
      .then(res => res.json())
      .then(data => {
        const counts = {};
        data.forEach(cat => {
          counts[cat.name] = cat.count;
        });
        setProductCounts(counts);
      })
      .catch(() => {});

    // Fetch tagged products
    const fetchTaggedProducts = async () => {
      try {
        const [offerta, novita, outlet] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/products?tag=offerta`),
          axios.get(`${BACKEND_URL}/api/products?tag=novita`),
          axios.get(`${BACKEND_URL}/api/products?tag=outlet`)
        ]);
        
        setOffertaProducts(offerta.data);
        setNovitaProducts(novita.data);
        setOutletProducts(outlet.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchTaggedProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dark-container pt-20">
      <SEO 
        title="ALLMX - Il Negozio per l'OffRoad che hai sempre sognato | Motocross Premium"
        description="Scopri oltre 300 prodotti premium per motocross: plastiche, freni, sospensioni, scarichi, lubrificanti e accessori delle migliori marche (KTM, Husqvarna, GasGas). Spedizione veloce dall'Italia. Prezzi competitivi e qualità professionale."
        keywords="motocross italia, ricambi moto cross, accessori motocross, plastiche KTM, freni moto, sospensioni enduro, scarichi motocross, lubrificanti moto, ALLMX, shop online motocross"
        url="https://allmx.it"
      />
      {/* Hero Section */}
      <section
        className="hero-section"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1606497058128-19b758a3dd88)',
        }}
      >
        <div className="hero-overlay"></div>
        
        {/* Hero Title and Subtitle */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-[7.6923%] text-center pt-16 sm:pt-20">
          <h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 leading-tight sm:leading-relaxed px-2"
            style={{
              textShadow: '3px 3px 12px rgba(0,0,0,1), 0 0 30px rgba(0,0,0,0.9), 0 0 60px rgba(0,0,0,0.7)',
              color: '#FFFFFF'
            }}
          >
            {t.home.heroTitle}
          </h1>
          <p 
            className="text-base sm:text-lg md:text-xl lg:text-2xl max-w-2xl mx-auto mb-6 sm:mb-10 px-2 text-center leading-relaxed" 
            style={{ 
              color: '#E0E0E0',
              textShadow: '2px 2px 8px rgba(0,0,0,1), 0 0 20px rgba(0,0,0,0.9)'
            }}
          >
            {t.home.heroSubtitle}
          </p>
        </div>
        
        {/* Search Bar - Hidden on mobile, visible on tablet+ */}
        <div className="hidden sm:block relative z-10 w-full px-4 sm:px-8 mb-6 sm:mb-8">
          <div className="max-w-2xl mx-auto">
            <SearchBar />
          </div>
        </div>

        {/* Category Icons - Horizontal scrollable */}
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-[7.6923%]">
          <div className="w-full max-w-6xl mx-auto mb-10 relative">
            {/* Scroll hint */}
            <p className="text-center text-sm mb-3" style={{ color: theme === 'light' ? '#666666' : 'rgba(255, 255, 255, 0.6)' }}>
              ← {language === 'it' ? 'Scorri per vedere tutte le categorie' : language === 'es' ? 'Desliza para ver todas las categorías' : 'Scroll to see all categories'} →
            </p>
            
            {/* Gradient fade indicators */}
            <div className="absolute left-0 top-12 bottom-0 w-12 z-10 pointer-events-none" 
              style={{ 
                background: theme === 'light' 
                  ? 'linear-gradient(to right, rgba(255,255,255,1), rgba(255,255,255,0))' 
                  : 'linear-gradient(to right, rgba(10,10,10,1), rgba(10,10,10,0))' 
              }} 
            />
            <div className="absolute right-0 top-12 bottom-0 w-12 z-10 pointer-events-none" 
              style={{ 
                background: theme === 'light' 
                  ? 'linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0))' 
                  : 'linear-gradient(to left, rgba(10,10,10,1), rgba(10,10,10,0))' 
              }} 
            />
            
            <div 
              ref={categoryScrollRef}
              className="overflow-x-auto overflow-y-hidden pb-4"
              style={{ 
                overscrollBehaviorX: 'contain',
                overscrollBehaviorY: 'none',
                WebkitOverflowScrolling: 'touch',
                maxWidth: '100%',
                scrollbarWidth: 'thin',
                scrollbarColor: '#FF6600 rgba(255, 255, 255, 0.1)'
              }}
            >
              <div className="flex gap-6 md:gap-6 justify-start" style={{ minWidth: 'max-content', width: 'max-content' }}>
                {allCategories.map((category) => {
                  const IconComponent = category.icon;
                  const productCount = productCounts[category.name] || 0;
                  const categoryName = language === 'it' ? category.name : language === 'es' ? (category.name_es || category.name_en) : category.name_en;
                  
                  return (
                    <Link
                      key={category.name}
                      to={`/products?category=${encodeURIComponent(category.name)}`}
                      className="group flex flex-col items-center gap-2 transition-transform hover:scale-110 w-20 md:w-24 flex-shrink-0"
                    >
                      <div 
                        className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center transition-all group-hover:shadow-lg group-hover:shadow-primary/50"
                        style={{ 
                          backgroundColor: 'rgba(255, 102, 0, 0.15)',
                          border: '2px solid rgba(255, 102, 0, 0.4)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                        }}
                      >
                        <IconComponent 
                          size={32} 
                          style={{ color: '#FF6600' }}
                          className="transition-transform group-hover:scale-110"
                        />
                      </div>
                      <span 
                        className="text-xs md:text-sm font-semibold text-center transition-colors group-hover:text-primary leading-tight"
                        style={{ 
                          color: theme === 'light' ? '#1a1a1a' : '#ffffff',
                          textShadow: theme === 'light' ? 'none' : '1px 1px 3px rgba(0,0,0,0.8)'
                        }}
                      >
                        {categoryName}
                      </span>
                      <span 
                        className="text-xs font-medium"
                        style={{ 
                          color: theme === 'light' ? '#666666' : 'rgba(255, 255, 255, 0.7)',
                          textShadow: theme === 'light' ? 'none' : '1px 1px 2px rgba(0,0,0,0.8)'
                        }}
                      >
                        ({productCount})
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* CTA Button and Links - Separate container */}
        <div className="relative z-10 w-full pb-8">
          <div className="flex flex-col items-center gap-4 px-4">
            <Link to="/products" className="btn-primary text-xl px-8 py-4">
              {language === 'it' ? 'Acquista Ora' : language === 'es' ? 'Comprar Ahora' : 'Shop Now'}
            </Link>
            <div className="flex gap-3 text-sm">
              <span style={{ color: 'var(--text-secondary)' }}>{language === 'it' ? 'Disponibile anche su' : language === 'es' ? 'También disponible en' : 'Also available on'}</span>
              <a
                href="https://www.amazon.it/s?me=A3526ZZLQNVU1W&marketplaceID=APJ6JRA9NG5V4"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold transition-colors underline"
                style={{ 
                  color: 'var(--text-primary)',
                  textShadow: '0 0 1px rgba(0,0,0,0.3)'
                }}
                onMouseEnter={(e) => e.target.style.color = 'var(--brand-primary)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--text-primary)'}
              >
                Amazon
              </a>
              <span style={{ color: 'var(--text-muted)' }}>•</span>
              <a
                href="https://www.ebay.it/str/allmx"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold transition-colors underline"
                style={{ 
                  color: 'var(--text-primary)',
                  textShadow: '0 0 1px rgba(0,0,0,0.3)'
                }}
                onMouseEnter={(e) => e.target.style.color = 'var(--brand-primary)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--text-primary)'}
              >
                eBay
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 🔥 Prodotti in Offerta - Carousel */}
      {!loading && offertaProducts.length > 0 && (
        <ProductCarousel 
          products={offertaProducts}
          title={language === 'it' ? '🔥 Prodotti in Offerta' : '🔥 Special Offers'}
          subtitle={language === 'it' ? 'Approfitta delle nostre migliori offerte' : 'Take advantage of our best offers'}
          itemsPerView={5}
        />
      )}

      {/* ✨ Novità - Carousel */}
      {!loading && novitaProducts.length > 0 && (
        <ProductCarousel 
          products={novitaProducts}
          title={language === 'it' ? '✨ Novità' : '✨ New Arrivals'}
          subtitle={language === 'it' ? 'I prodotti più recenti del nostro catalogo' : 'The latest products in our catalog'}
          itemsPerView={5}
        />
      )}

      {/* 💰 Outlet - Carousel */}
      {!loading && outletProducts.length > 0 && (
        <ProductCarousel 
          products={outletProducts}
          title={language === 'it' ? '💰 Outlet' : '💰 Outlet'}
          subtitle={language === 'it' ? 'Prezzi scontati su prodotti selezionati' : 'Discounted prices on selected products'}
          itemsPerView={5}
        />
      )}

      {/* Why ALLMX Section - SPOSTATO QUI (prima del footer) */}
      <section className="py-20 px-6 lg:px-[7.6923%]" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-[1400px] mx-auto">
          <h2 className="display-medium text-center mb-16">{t.home.whyTitle}</h2>
          <div className="dark-grid">
            <div className="dark-card p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: 'var(--brand-hover)' }}>
                <Zap size={32} style={{ color: 'var(--brand-primary)' }} />
              </div>
              <h3 className="text-2xl font-semibold mb-4">{t.home.whyPerformance}</h3>
              <p className="body-medium">
                {t.home.whyPerformanceDesc}
              </p>
            </div>

            <div className="dark-card p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: 'var(--brand-hover)' }}>
                <Shield size={32} style={{ color: 'var(--brand-primary)' }} />
              </div>
              <h3 className="text-2xl font-semibold mb-4">{t.home.whyDurability}</h3>
              <p className="body-medium">
                {t.home.whyDurabilityDesc}
              </p>
            </div>

            <div className="dark-card p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: 'var(--brand-hover)' }}>
                <Truck size={32} style={{ color: 'var(--brand-primary)' }} />
              </div>
              <h3 className="text-2xl font-semibold mb-4">{t.home.whyDelivery}</h3>
              <p className="body-medium">
                {t.home.whyDeliveryDesc}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Trust - RIMOSSO (spostato sopra) */}
      {/* Why ALLMX - RIMOSSO (spostato sopra) */}

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-[7.6923%]" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="display-large mb-6">{t.home.ctaTitle}</h2>
          <p className="body-large mb-10" style={{ color: 'var(--text-secondary)' }}>
            {t.home.ctaSubtitle}
          </p>
          <div className="flex flex-col items-center gap-4">
            <Link to="/products" className="btn-primary text-xl px-8 py-4">
              {language === 'it' ? 'Scopri i Prodotti' : 'Discover Products'}
            </Link>
            <div className="flex gap-3 text-sm">
              <span className="text-white/50">{language === 'it' ? 'o visita' : 'or visit'}</span>
              <a
                href="https://www.amazon.it/s?me=A3526ZZLQNVU1W&marketplaceID=APJ6JRA9NG5V4"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white underline transition-colors"
              >
                Amazon
              </a>
              <span className="text-white/40">•</span>
              <a
                href="https://www.ebay.it/str/allmx"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white underline transition-colors"
              >
                eBay
              </a>
            </div>
          </div>
        </div>
      </section>

      <PromoVideo id="video" />
    </div>
  );
};

export default Home;
